"use client"

import Image, { type ImageProps } from "next/image"

import { directusImageLoader, isIndexableLocalAsset } from "@/lib/image-utils"

/**
 * next/image for canonical same-origin image URLs, usable from a server
 * component.
 *
 * A server component cannot pass `loader` to next/image directly -- functions
 * are not serializable across the boundary, and the build fails while
 * prerendering. Keeping the loader on this side of a "use client" file lets the
 * server pass only the src string.
 *
 * A local /public/examples asset takes the second branch: it is already served
 * at its own indexable URL, so it renders `unoptimized` and keeps the bare
 * /examples path in src rather than being rewritten to the robots-disallowed
 * /_next/image. This is the mixed-source case -- guide, weed, pest and city
 * heroes pass either a Directus /img/ path or an /examples path depending on
 * whether an admin set a hero row -- so the branch has to live here.
 *
 * Any other src (a third-party image_url, some other /public asset) still falls
 * through to Next's own optimizer, unchanged.
 */
export function CanonicalImage(props: ImageProps) {
  const isCanonical =
    typeof props.src === "string" && props.src.startsWith("/img/")

  if (isIndexableLocalAsset(props.src)) {
    return <Image {...props} unoptimized />
  }

  return <Image {...props} loader={isCanonical ? directusImageLoader : undefined} />
}
